// Models
import { IExerciseData } from 'storage/exercise/models'
import {
  IModelInput,
  IWorkoutSetInput,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'

// React
import {
  FC,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFormContext } from 'react-hook-form'

// Misc
import { ThemeContext } from 'styled-components'
import { buttonClickTracking } from 'utils/tracking'
import { uid } from 'utils/functions'

// Components
import * as Styled from './styled'
import { Aligner, Icon } from 'heeds-ds'
import { SpecialSeriesFormTag, SeriesFormTag } from 'components'

// Constants
const EMPTY_SPECIAL_SERIE: TSpecialSetInput = {
  id: '',
  name: '',
  type: 'super_set',
  interval: '',
  quantity: '',
  exercises: {},
  comments: '',
}

type Props = {
  formIndex: number
  onEditSpecialSet?: (workoutSet: TSpecialSetInput) => void
  openExerciseGifModal: (exercise: IExerciseData) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  removeWorkoutSet: (modelId: string, workoutSetId: string) => void
  replicateWorkoutSetFields?: (modelId: string, set: IWorkoutSetInput) => void
  selectedModelIndex?: number
  setSpecialSet: TSetSpecialSet
  specialSet?: TSpecialSetInput
  stateModels?: IModelInput[]
}

const Desktop: FC<Props> = ({
  formIndex,
  onEditSpecialSet,
  openExerciseGifModal,
  removeExerciseFromWorkoutSet,
  removeWorkoutSet,
  replicateWorkoutSetFields,
  selectedModelIndex,
  setSpecialSet,
  specialSet,
  stateModels,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const theme = useContext(ThemeContext)
  const newLength = useMemo(() => {
    return [
      Object.keys(stateModels?.[selectedModelIndex ?? 0]?.workout_set ?? {})
        .length,
      selectedModelIndex ?? 0,
    ]
  }, [selectedModelIndex, stateModels])
  const [oldLength, setOldLength] = useState(newLength)

  const { unregister } = useFormContext()

  const hasModels = stateModels?.length
  const selectedModelFound =
    hasModels &&
    stateModels.filter((_, index) => index === selectedModelIndex).length > 0

  const addSpecialSet = () => {
    setSpecialSet({
      ...EMPTY_SPECIAL_SERIE,
      id: uid(),
      name: 'Série Especial',
    })
  }

  const removeSet = (setId: string) => {
    if (stateModels) {
      const modelId = stateModels[formIndex].id
      removeWorkoutSet(modelId, setId)
      unregister(`models.${formIndex}.workout_set.${setId}`)
    }
  }

  const removeExerciseFromSet = (exerciseIndex: number, setIndex: number) => {
    if (stateModels) {
      const modelId = stateModels[formIndex].id
      const setId = stateModels[formIndex].workout_set[setIndex].id
      removeExerciseFromWorkoutSet(modelId, setId, exerciseIndex)
      unregister(
        `models.${formIndex}.workout_set.${
          Object.values(stateModels[formIndex].workout_set).length - 1
        }`,
      )
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!specialSet)
        buttonRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)

    return () => clearTimeout(timeout)
  }, [specialSet])

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      if (oldLength[0] < newLength[0] && oldLength[1] === newLength[1])
        buttonRef?.current?.scrollIntoView({ behavior: 'smooth' })

      setOldLength(newLength)
    }, 100)

    return () => clearTimeout(timeout)
  }, [oldLength, newLength])

  return (
    <Styled.SeriesContainer data-testid="workout-sets-desktop-container">
      <Aligner data-testid="workout-sets-desktop-list" flex="column" gap="16px">
        {!hasModels || !selectedModelFound || newLength[0] === 0 ? (
          <Aligner justify="center" padding="160px 0 0">
            <Styled.EmptySet>
              Você pode criar o seu treino do zero escolhendo os exercícios aqui
              ao lado.
            </Styled.EmptySet>
          </Aligner>
        ) : (
          hasModels &&
          stateModels?.map((stateModel, modelIndex) => {
            return Object.values(stateModel.workout_set)?.map(
              (stateSet, setIndex) => {
                const prefixName = `models.${modelIndex}.workout_set.${stateSet.id}`

                if (stateSet.type === 'normal') {
                  return (
                    <SeriesFormTag
                      replicateWorkoutSetFields={(set) =>
                        replicateWorkoutSetFields?.(stateModel.id, set)
                      }
                      displayMode
                      key={stateSet.id}
                      exercise={stateSet?.exercises[0]?.exercise}
                      name={prefixName}
                      onClickImage={openExerciseGifModal}
                      onRemove={() => removeSet(stateSet.id)}
                      showBorder
                      visible={modelIndex === selectedModelIndex}
                    />
                  )
                }

                return (
                  <SpecialSeriesFormTag
                    replicateWorkoutSetFields={(set) =>
                      replicateWorkoutSetFields?.(stateModel.id, set)
                    }
                    displayMode
                    key={stateSet.id}
                    name={prefixName}
                    onClickImage={openExerciseGifModal}
                    onEdit={onEditSpecialSet}
                    removeWorkoutSet={() => removeSet(stateSet.id)}
                    removeExerciseFromSpecialWorkoutSet={(exerciseIndex) => {
                      removeExerciseFromSet(exerciseIndex, setIndex)
                    }}
                    setSpecialSet={setSpecialSet}
                    showBorder
                    visible={modelIndex === selectedModelIndex}
                    workoutSet={{ ...stateSet, index: setIndex }}
                  />
                )
              },
            )
          })
        )}
      </Aligner>

      <Styled.AddButton
        onClick={addSpecialSet}
        ref={buttonRef}
        track={buttonClickTracking}
        trackName="show_special_workout_set_form"
      >
        <Icon iconName="add" color={theme.colors.interactive.default} />
        Adicionar série especial
      </Styled.AddButton>
    </Styled.SeriesContainer>
  )
}

export default Desktop
